import React, { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import QRCode from 'qrcode.react';
import ElementContainer from 'components/shared/ElementContainer';
import TeacherComment from 'components/shared/TeacherComment';
import Modal from 'components/shared/Modal';

const DESCRIPTION_MAX_LENGTH = 5000;

const MixedMediaArtifact = (props) => {
  const {
    title,
    titleColor,
    prompt,
    url,
    validFormats,
    token,
    requirement,
    requirable,
    reviewable,
    comment,
    id,
    user,
  } = props;

  const [mixedMedia, setMixedMedia] = useState(props.media);
  const [completed, setCompleted] = useState(props.completed);
  const [status, setStatus] = useState(props.status);
  const [upload, setUpload] = useState(null);
  const [error, setError] = useState(null);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [description, setDescription] = useState('');
  const [display, setDisplay] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [showFormats, setShowFormats] = useState(false);

  const ErrorMessage = (props) => {
    const { error } = props;
    if (error) {
      return (
        <div className="text-center danger radius">
          <p>{error}</p>
        </div>
      );
    } else {
      return null;
    }
  };

  const cs2nEndpoint = axios.create({
    headers: {
      authorization: `bearer ${token}`,
      'content-type': 'multipart/form-data',
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    // Final check before uploading
    if (!upload) {
      setError('Please select a file to upload.');
      return;
    }

    if (description.length > DESCRIPTION_MAX_LENGTH) {
      setError(`Description must be ${DESCRIPTION_MAX_LENGTH.toLocaleString()} characters or fewer.`);
      return;
    }

    // Extract the file extension (lowercase)
    const fileExtension = upload.name.split('.').pop().toLowerCase();

    // Clean and sanitize validFormats
    const allowedFormats = validFormats
      .replace(/\band\b/gi, '') // Remove "and"
      .split(',') // Split on commas
      .map((f) => f.replace(/\./g, '').trim().toLowerCase()) // Remove dots and trim spaces
      .filter(Boolean); // Remove any empty strings

    // Validate file extension
    if (!allowedFormats.includes(fileExtension)) {
      setError(
        `Invalid file type. Allowed types: ${allowedFormats.join(', ')}.`
      );
      return;
    }

    setWaitingForResponse(true);
    setError(null);

    let data = new FormData();
    data.set('mixed_media[upload]', upload);
    data.set('mixed_media[description]', description);
    data.set('requirement_id', requirement.id);

    cs2nEndpoint
      .post('/client_api/v1/mixed_medias', data)
      .then((r) => {
        const responseData = {
          ...r.data.artifact,
          description: r.data.description,
          upload_filename: upload.name,
          upload_url: r.data.artifact.direct_url,
          upload_content_type: upload.type,
        };
        setMixedMedia(responseData);
        setCompleted(r.data.completed);
        setStatus(r.data.status);
        setDisplay(false);
        setError(null);
        setWaitingForResponse(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || 'Upload failed. Please try again.');
        setWaitingForResponse(false);
      });
  };

  const ModalHeader = (props) => {
    const { title } = props;

    const span = {
      position: 'absolute',
      top: 15,
      right: 15,
    };

    const btn = {
      cursor: 'pointer',
      color: 'lightgray',
    };

    return (
      <div className="modal-header">
        <h2 className="text-center">{title}</h2>
        <span style={span}>
          <a onClick={() => setDisplay(false)} style={btn}>
            <FaTimes className="icon-medium" />
          </a>
        </span>
      </div>
    );
  };

  return (
    <ElementContainer
      id={id}
      user={user}
      token={token}
      color={titleColor}
      title={title}
      requirable={requirable}
      reviewable={reviewable}
      status={status}
      completed={completed}
      icon={'bx-upload'}
    >
      <div className="m-bottom-1">{prompt}</div>

      {mixedMedia && !error ? (
        <div className="mixed-media-artifact-state">
          <ArtifactUpload
            mixedMedia={mixedMedia}
            waitingForResponse={waitingForResponse}
            color={titleColor}
            reviewable={reviewable}
            status={status}
            comment={comment}
          />
          <button
            type="button"
            className="mixed-media-replace-button"
            onClick={() => setDisplay(true)}
          >
            <i className="bx bx-refresh" aria-hidden="true" />
            Replace File
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="mixed-media-upload-card"
          onClick={() => setDisplay(true)}
          disabled={waitingForResponse}
        >
          <span className="mixed-media-upload-card-icon">
            <i className="bx bx-cloud-upload" aria-hidden="true" />
          </span>
          <span className="mixed-media-upload-card-copy">
            <strong>
              {waitingForResponse ? 'Uploading...' : 'Upload File'}
            </strong>
            <span>
              {waitingForResponse
                ? 'Please wait while your file is uploaded.'
                : 'Choose a file from this device or upload from your phone.'}
            </span>
          </span>
          <span className="mixed-media-upload-card-action" aria-hidden="true">
            <i className="bx bx-chevron-right" />
          </span>
        </button>
      )}

      <Modal display={display} modalId={requirement.id}>
        <ModalHeader title={title} />
        <div className="modal-body mixed-media-upload-modal">
          <ErrorMessage error={error} />

          <div className="upload-options">
            <div className="upload-main">
              <h3>Upload from this device</h3>
              <p className="upload-help">Choose a file up to 500 MB.</p>

              <label className="text-bold">File</label>
              <div className="upload-file-field">
                <input
                  type="file"
                  onChange={(e) => {
                    setUpload(e.target.files[0]);
                    setError(null);
                  }}
                />
              </div>

              <label className="text-bold">Description</label>
              <textarea
                className="expand"
                placeholder="Optional description"
                value={description}
                maxLength={DESCRIPTION_MAX_LENGTH}
                aria-describedby="mixed-media-description-count"
                onChange={(e) => setDescription(e.target.value)}
              />

              <div id="mixed-media-description-count" className="text-right" aria-live="polite" style={{ color: description.length >= DESCRIPTION_MAX_LENGTH ? '#b42318' : description.length >= DESCRIPTION_MAX_LENGTH * 0.9 ? '#966000' : 'inherit' }}>
                {description.length.toLocaleString()} / {DESCRIPTION_MAX_LENGTH.toLocaleString()} characters
              </div>

              <div className="accepted-types-wrapper">
                <button
                  type="button"
                  className="accepted-types-toggle"
                  onClick={() => setShowFormats(!showFormats)}
                >
                  View accepted file types
                </button>

                {showFormats && (
                  <div className="accepted-types-popover">{validFormats}</div>
                )}
              </div>

              <a
                onClick={handleSubmit}
                className={
                  waitingForResponse
                    ? 'btn btn-secondary radius expand'
                    : 'btn radius expand'
                }
              >
                {waitingForResponse ? 'Uploading...' : 'Upload File'}
              </a>
            </div>

            <div className="upload-phone">
              <h3>Upload from your phone</h3>

              <p className="upload-help">
                Scan the QR code to choose a file from your phone or tablet.
              </p>

              <div className="qr-wrapper">
                <QRCode value={url} className="qr-code" />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </ElementContainer>
  );
};

const ArtifactUpload = (props) => {
  const mixedMedia = props.mixedMedia;
  const status = props.status;
  const reviewable = props.reviewable;
  const comment = props.comment;
  const waitingForResponse = props.waitingForResponse;

  const uploadUrl = mixedMedia?.upload_url;
  const filename = mixedMedia?.upload_filename;
  const contentType = mixedMedia?.upload_content_type;
  const isImage = contentType && contentType.includes('image');
  const isVideo = contentType && contentType.includes('video');

  if (waitingForResponse) {
    return (
      <div className="mixed-media-file-card is-uploading">
        <span className="mixed-media-file-icon">
          <i className="bx bx-loader-alt bx-spin" aria-hidden="true" />
        </span>
        <span className="mixed-media-file-copy">
          <strong>Uploading file...</strong>
          <span>Please wait while your upload completes.</span>
        </span>
      </div>
    );
  }

  return (
    <div className="mixed-media-file-card">
      <a
        href={uploadUrl}
        className="mixed-media-file-preview"
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open ${filename}`}
      >
        {isImage ? (
          <img src={uploadUrl} alt="" />
        ) : (
          <i
            className={isVideo ? 'bx bxs-video' : 'bx bxs-file'}
            aria-hidden="true"
          />
        )}
      </a>

      <div className="mixed-media-file-copy">
        <strong title={filename}>{filename}</strong>
        <span>
          {mixedMedia.description == ''
            ? 'Uploaded file'
            : mixedMedia.description}
        </span>
        {reviewable && comment ? (
          <TeacherComment comment={comment} status={status} />
        ) : null}
      </div>

      <a
        href={uploadUrl}
        className="mixed-media-file-download"
        target="_blank"
        rel="noopener noreferrer"
      >
        <i className="bx bx-download" aria-hidden="true" />
        <span>View / Download</span>
      </a>
    </div>
  );
};

export default MixedMediaArtifact;
